import React, { createContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { registerAccessDeniedHandler } from '../utils/accessDenied';
import AccessDeniedModal from '../components/AccessDeniedModal';

export const AuthContext = createContext();

function normalizeRights(val) {
  if (Array.isArray(val)) return val;
  if (!val) return [];

  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') {
        return Object.keys(parsed).filter((k) => !!parsed[k]);
      }
      return [];
    } catch (_) {
      return val.split(',').map((s) => s.trim()).filter(Boolean);
    }
  }

  if (typeof val === 'object') {
    return Object.keys(val).filter((k) => !!val[k]);
  }

  return [];
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [showDenied, setShow] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    registerAccessDeniedHandler(() => setShow(true));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me')
        .then((res) => {
          const safeUser = {
            ...res.data,
            rights: normalizeRights(res.data?.rights),
          };
          setUser(safeUser);
        })
        .catch(() => logout());
    }
  }, []);

  const login = async (creds) => {
    const res = await api.post('/auth/login', creds);
    localStorage.setItem('token', res.data.token);

    const me = await api.get('/auth/me');
    const safeUser = {
      ...me.data,
      rights: normalizeRights(me.data?.rights),
    };
    setUser(safeUser);
  };

  const handleDenied = () => {
    setShow(false);
    logout();
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const hasRight = (code) => {
    const rights = normalizeRights(user?.rights);
    return rights.includes(code);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, hasRight }}>
      {children}
      <AccessDeniedModal show={showDenied} onConfirm={handleDenied} />
    </AuthContext.Provider>
  );
}